/**
 * Theme Toggle Component
 * Provides UI for switching between available themes
 */

import React from 'react';
import { useTheme } from '../../theme/providers/ThemeProvider';
import { Sun, Moon } from 'lucide-react';

interface ThemeToggleProps {
  className?: string;
  showLabels?: boolean;
  variant?: 'button' | 'select' | 'toggle';
  size?: 'small' | 'medium' | 'large';
}

export const ThemeToggle: React.FC<ThemeToggleProps> = ({
  className = '',
  showLabels = true,
  variant = 'select',
  size = 'medium',
}) => {
  const { currentTheme, availableThemes, setTheme } = useTheme();

  // Debug logging (remove in production)
  // console.log('🎛️ ThemeToggle rendering:', { variant, size, currentTheme: currentTheme?.metadata?.name });

  const handleThemeChange = (themeName: string) => {
    if (themeName !== currentTheme.metadata.name) {
      setTheme(themeName);
    }
  };

  if (variant === 'button') {
    return (
      <div className={`theme-toggle theme-toggle--button ${className}`}>
        {showLabels && (
          <span className="theme-toggle__label">Theme:</span>
        )}
        <div className="theme-toggle__buttons">
          {availableThemes.map((theme) => (
            <button
              key={theme.metadata.name}
              className={`theme-toggle__button ${
                currentTheme.metadata.name === theme.metadata.name 
                  ? 'theme-toggle__button--active' 
                  : ''
              } theme-toggle__button--${size}`}
              onClick={() => handleThemeChange(theme.metadata.name)}
              title={theme.metadata.description}
            >
              {theme.metadata.displayName}
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (variant === 'toggle' && availableThemes.length === 2) {
    const isDark = currentTheme.metadata.isDark;
    const toggleTheme = availableThemes.find(theme => theme.metadata.isDark !== isDark);

    return (
      <div className={`theme-toggle theme-toggle--toggle ${className}`}>
        {showLabels && (
          <span className="theme-toggle__label">
            {isDark ? 'Dark' : 'Light'} Theme
          </span>
        )}
        <button
          className={`theme-toggle__switch theme-toggle__switch--${size}`}
          onClick={() => toggleTheme && handleThemeChange(toggleTheme.metadata.name)}
          title={`Switch to ${toggleTheme?.metadata.displayName || 'other theme'}`}
          aria-label={`Switch to ${toggleTheme?.metadata.displayName || 'other theme'}`}
        >
          <span
            className={`theme-toggle__slider ${isDark ? 'theme-toggle__slider--dark' : 'theme-toggle__slider--light'}`}
          >
            {isDark ? <Moon size={12} /> : <Sun size={12} />}
          </span>
        </button>
      </div>
    );
  }

  // Default: select variant
  return (
    <div className={`theme-toggle theme-toggle--select ${className}`}>
      {showLabels && (
        <label htmlFor="theme-select" className="theme-toggle__label">
          Theme:
        </label>
      )}
      <select
        id="theme-select"
        className={`theme-toggle__select theme-toggle__select--${size}`}
        value={currentTheme.metadata.name}
        onChange={(e) => handleThemeChange(e.target.value)}
        title="Select application theme"
      >
        {availableThemes.map((theme) => (
          <option
            key={theme.metadata.name}
            value={theme.metadata.name}
          >
            {theme.metadata.displayName}
          </option>
        ))}
      </select>
    </div>
  );
};

export default ThemeToggle;
